import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Monitor, Smartphone, Settings as SettingsIcon, Sparkles, Download,
  CheckCircle2, XCircle, AlertCircle, Loader2,
} from 'lucide-react'
import toast from 'react-hot-toast'
import PageTransition from '@/components/PageTransition'
import GlassCard from '@/components/GlassCard'
import { useSettingsStore } from '@/store/settings'
import { aiApi } from '@/api/client'
import type { DeviceType, OllamaPullEvent } from '@/api/client'
import { useAiHealth } from '@/hooks/useAiHealth'

export default function SettingsPage() {
  const {
    defaultDevice,
    aiEnabled,
    aiModel,
    diffView,
    setDefaultDevice,
    setAiEnabled,
    setAiModel,
    setDiffView,
  } = useSettingsStore()

  // ── AI: health + model list + pull state ─────────────────────────────────
  const health = useAiHealth()
  const models = useQuery({
    queryKey: ['ai-models'],
    queryFn: aiApi.listModels,
    enabled: health.data?.available === true,
  })

  const [pullTarget, setPullTarget] = useState('')
  const [pullEvent, setPullEvent] = useState<OllamaPullEvent | null>(null)
  const [pulling, setPulling] = useState(false)
  const abortRef = useRef<(() => void) | null>(null)

  // Initialise the pull-target input from the configured-or-stored model
  useEffect(() => {
    if (!pullTarget && !pulling) {
      const initial = aiModel || health.data?.configuredModel || ''
      if (initial) setPullTarget(initial)
    }
  }, [aiModel, health.data?.configuredModel, pullTarget, pulling])

  const startPull = () => {
    const name = pullTarget.trim()
    if (!name || pulling) return
    setPulling(true)
    setPullEvent({ status: 'starting…', digest: null, total: null, completed: null, error: null })
    abortRef.current = aiApi.pullModel(name, {
      onEvent: ev => setPullEvent(ev),
      onDone: () => {
        setPulling(false)
        abortRef.current = null
        toast.success(`${name} ready.`)
        models.refetch()
        health.refetch()
      },
      onError: msg => {
        setPulling(false)
        abortRef.current = null
        setPullEvent({ status: null, digest: null, total: null, completed: null, error: msg })
        toast.error(`Pull failed: ${msg}`)
      },
    })
  }

  const cancelPull = () => {
    abortRef.current?.()
    abortRef.current = null
    setPulling(false)
    setPullEvent(null)
  }

  // Clean up on unmount
  useEffect(() => () => abortRef.current?.(), [])

  const pullPercent = pullEvent && pullEvent.total && pullEvent.completed
    ? Math.min(100, Math.round((pullEvent.completed / pullEvent.total) * 100))
    : null

  const QWEN_CODER_TAGS = [
    'qwen2.5-coder:1.5b-instruct',
    'qwen2.5-coder:3b-instruct',
    'qwen2.5-coder:7b-instruct',
    'qwen2.5-coder:14b-instruct',
    'qwen2.5-coder:32b-instruct',
  ]

  return (
    <PageTransition>
      <div className="max-w-2xl mx-auto px-4 py-12 space-y-6">
        <div className="flex items-center gap-2 mb-2">
          <SettingsIcon size={20} className="text-accent" />
          <h1 className="text-xl font-semibold">Settings</h1>
        </div>

        <GlassCard>
          <h2 className="text-sm font-semibold mb-1">Default device</h2>
          <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>
            Used as the initial selection on the audit form.
          </p>
          <div className="flex gap-2">
            {(['DESKTOP', 'MOBILE'] as DeviceType[]).map(d => (
              <button
                key={d}
                type="button"
                onClick={() => {
                  setDefaultDevice(d)
                  toast.success(`Default device set to ${d.toLowerCase()}`)
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 ${
                  defaultDevice === d
                    ? 'bg-accent/15 border-accent/40 text-accent'
                    : 'bg-white/5 border-white/10 text-[var(--text-secondary)] hover:border-white/20'
                }`}
              >
                {d === 'DESKTOP' ? <Monitor size={14} /> : <Smartphone size={14} />}
                {d.charAt(0) + d.slice(1).toLowerCase()}
              </button>
            ))}
          </div>
        </GlassCard>

        <GlassCard>
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <Sparkles size={14} className="text-accent" />
              <h2 className="text-sm font-semibold">AI suggestions</h2>
            </div>
            <label className="flex items-center gap-2 cursor-pointer text-xs"
              style={{ color: 'var(--text-secondary)' }}>
              <span>{aiEnabled ? 'Enabled' : 'Disabled'}</span>
              <span
                className={`relative w-8 h-4 rounded-full transition-colors ${
                  aiEnabled ? 'bg-accent' : 'bg-white/10'
                }`}
              >
                <input
                  type="checkbox"
                  checked={aiEnabled}
                  onChange={e => setAiEnabled(e.target.checked)}
                  className="sr-only"
                />
                <span
                  className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${
                    aiEnabled ? 'left-4' : 'left-0.5'
                  }`}
                />
              </span>
            </label>
          </div>
          <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>
            Local LLM analyses Lighthouse findings against your source code and proposes patches.
            Inference runs on this machine via Ollama. Source code never leaves your machine.
          </p>

          {/* Health pill */}
          <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-md bg-white/[0.02] border border-white/10">
            {health.isLoading ? (
              <Loader2 size={12} className="animate-spin text-[var(--text-muted)]" />
            ) : health.data?.available ? (
              health.data.modelLoaded ? (
                <CheckCircle2 size={12} style={{ color: 'var(--score-good)' }} />
              ) : (
                <AlertCircle size={12} style={{ color: 'var(--score-average)' }} />
              )
            ) : (
              <XCircle size={12} style={{ color: 'var(--score-poor)' }} />
            )}
            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
              {health.isLoading
                ? 'Checking Ollama…'
                : health.data?.available
                  ? health.data.modelLoaded
                    ? `Ollama ready — ${health.data.configuredModel} loaded (${health.data.latencyMs} ms)`
                    : `Ollama up but ${health.data.configuredModel} not pulled yet`
                  : health.data?.error || 'Ollama unreachable'}
            </span>
          </div>

          {/* Default model selection */}
          <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>
            Default model
          </label>
          {models.data && models.data.length > 0 ? (
            <select
              value={aiModel}
              onChange={e => setAiModel(e.target.value)}
              className="w-full mb-3 px-3 py-2 rounded-md text-xs font-mono bg-white/5 border border-white/10 text-[var(--text-primary)] focus:border-accent/60 focus:outline-none transition-colors"
            >
              <option value="">Use BFF default ({health.data?.configuredModel ?? '—'})</option>
              <optgroup label="Installed">
                {models.data.map(m => (
                  <option key={m.name} value={m.name}>{m.name}</option>
                ))}
              </optgroup>
              {(() => {
                const installed = new Set(models.data.map(m => m.name))
                const notInstalled = QWEN_CODER_TAGS.filter(t => !installed.has(t))
                if (notInstalled.length === 0) return null
                return (
                  <optgroup label="qwen2.5-coder (not pulled)">
                    {notInstalled.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </optgroup>
                )
              })()}
            </select>
          ) : (
            <input
              type="text"
              value={aiModel}
              onChange={e => setAiModel(e.target.value)}
              placeholder={health.data?.configuredModel ?? 'qwen2.5-coder:7b-instruct'}
              className="w-full mb-3 px-3 py-2 rounded-md text-xs font-mono bg-white/5 border border-white/10 text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-accent/60 focus:outline-none transition-colors"
            />
          )}

          {/* Pull model */}
          <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>
            Pull a model
          </label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={pullTarget}
              onChange={e => setPullTarget(e.target.value)}
              placeholder="qwen2.5-coder:7b-instruct"
              disabled={pulling}
              list="qwen-coder-tags"
              className="flex-1 px-3 py-2 rounded-md text-xs font-mono bg-white/5 border border-white/10 text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-accent/60 focus:outline-none transition-colors disabled:opacity-50"
            />
            <datalist id="qwen-coder-tags">
              {QWEN_CODER_TAGS.map(t => (
                <option key={t} value={t} />
              ))}
            </datalist>
            {pulling ? (
              <button
                type="button"
                onClick={cancelPull}
                className="flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-md bg-red-500/15 border border-red-500/40 text-red-300 hover:bg-red-500/25 transition-colors"
              >
                Cancel
              </button>
            ) : (
              <button
                type="button"
                onClick={startPull}
                disabled={!pullTarget.trim() || !health.data?.available}
                className="flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-md bg-accent/15 border border-accent/40 text-accent hover:bg-accent/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Download size={11} /> Pull
              </button>
            )}
          </div>

          {pullEvent && (
            <div className="px-3 py-2 rounded-md bg-white/[0.02] border border-white/10 mb-1">
              {pullEvent.error ? (
                <p className="text-xs text-red-400">{pullEvent.error}</p>
              ) : (
                <>
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span style={{ color: 'var(--text-secondary)' }}>
                      {pullEvent.status ?? 'Working…'}
                    </span>
                    {pullPercent !== null && (
                      <span className="tabular-nums font-mono" style={{ color: 'var(--text-muted)' }}>
                        {pullPercent}%
                      </span>
                    )}
                  </div>
                  <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                    <div
                      className="h-full transition-all duration-300"
                      style={{
                        width: pullPercent !== null ? `${pullPercent}%` : '20%',
                        background: 'var(--accent)',
                        animation: pullPercent === null ? 'pulse 2s infinite' : undefined,
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          )}

          <p className="text-[11px] mt-2" style={{ color: 'var(--text-muted)' }}>
            First pull downloads several GB. PATs are never persisted.
            On macOS use native Ollama (<code>brew install --cask ollama</code>) for GPU access — Docker
            on Apple Silicon is CPU-only.
          </p>
        </GlassCard>

        <GlassCard>
          <h2 className="text-sm font-semibold mb-2">Patch diff layout</h2>
          <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>
            How AI-suggested patches render. Applies to every suggestion.
          </p>
          <div className="flex gap-2">
            {(['unified', 'side-by-side'] as const).map(view => (
              <button
                key={view}
                type="button"
                onClick={() => setDiffView(view)}
                className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
                  diffView === view
                    ? 'border-accent/60 text-accent bg-accent/10'
                    : 'border-white/10 text-[var(--text-secondary)] hover:border-white/20'
                }`}
              >
                {view === 'unified' ? 'Unified' : 'Side-by-side'}
              </button>
            ))}
          </div>
        </GlassCard>

      </div>
    </PageTransition>
  )
}
